import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import {
  APIProvider,
  ControlPosition,
  MapControl,
  AdvancedMarker,
  Map,
  useMap,
  useMapsLibrary,
  useAdvancedMarkerRef,
  AdvancedMarkerRef,
} from "@vis.gl/react-google-maps";
import styled from "styled-components";

const StyledItem = styled.div`
  width: 300px;

  & input {
    width: 100%;
    height: 40px;
    padding: 0 12px;
    font-size: 18px;
  }

  & .custom-list {
    width: 100%;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  & .custom-list-item {
    padding: 8px;
  }

  & .custom-list-item:hover {
    background: lightgrey;
    cursor: pointer;
  }
`;

const App = ({
  onAdd,
  setAddress,
}: {
  onAdd: (marker: any) => void;
  setAddress: (address: string) => void;
}) => {
  const [selectedPlace, setSelectedPlace] =
    useState<google.maps.places.PlaceResult | null>(null);

  return (
    <>
      <MapControl position={ControlPosition.TOP}>
        <div className="autocomplete-control">
          <StyledItem>
            <PlaceAutocomplete onPlaceSelect={setSelectedPlace} />
          </StyledItem>
        </div>
      </MapControl>
      <MapHandler place={selectedPlace} onAdd={onAdd} setAddress={setAddress} />
    </>
  );
};

interface MapHandlerProps {
  place: google.maps.places.PlaceResult | null;
  onAdd: any;
  setAddress: (address: string) => void;
}

const MapHandler = ({ place, onAdd, setAddress }: MapHandlerProps) => {
  const map = useMap();
  const advancedMarkerLibrary = useMapsLibrary("marker");

  useEffect(() => {
    if (!map || !place || !advancedMarkerLibrary) return;

    if (place.geometry?.viewport) {
      map.fitBounds(place.geometry?.viewport);
    }
    const marker = new google.maps.Marker({
      position: place.geometry?.location,
      draggable: true,
    });
    marker.setMap(map);
    (marker as any).formatted_address = place.formatted_address;
    onAdd(marker);

    (async () => {
      // if (
      //   !(await (window as any).confirmDialog(
      //     "Use address?",
      //     "Do you want to save this address in contact?"
      //   ))
      // ) {
      //   return;
      // }
      setAddress(place.formatted_address || "");
    })();

    return () => {
      marker.setMap(null);
    };
  }, [map, place, advancedMarkerLibrary]);

  return null;
};

interface PlaceAutocompleteProps {
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  className?: string;
}

export const PlaceAutocomplete = ({
  onPlaceSelect,
  className,
}: PlaceAutocompleteProps) => {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary("places");

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ["geometry", "name", "formatted_address"],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    placeAutocomplete.addListener("place_changed", () => {
      onPlaceSelect(placeAutocomplete.getPlace());
    });
  }, [onPlaceSelect, placeAutocomplete]);

  return <input ref={inputRef} className={className} />;
};

export default App;
